import React, { useState, useEffect } from 'react';
import PropTypes  from 'prop-types';
import get from 'lodash/get'
import { Row, Col, Button } from 'reactstrap'
import { TextField, InputAdornment, IconButton, Switch } from '@material-ui/core'
import { Field, reduxForm } from 'redux-form';
import { DatePicker, MuiPickersUtilsProvider } from "material-ui-pickers"
import moment from "moment"
import DateFnsUtils from '@date-io/moment'
import 'moment/locale/es'

// Components
import LoadingButton from '../../../../components/LoadingButton';

// Redux
import { connect } from 'react-redux';
import { promptUser } from '../../../../redux/actions/notificationsActions';
import { integrateDownloads } from '../../../../redux/actions/integrationsActions';

// Libs
import { segmentTrackEvent } from '../../../../libs/utils';

// Icons
import EyeIcon from 'mdi-react/EyeIcon';
import EyeOffIcon from 'mdi-react/EyeOffIcon';
import HelpCircleIcon from 'mdi-react/HelpCircleIcon';

// Img
import { SAT_IMG } from '../../../../constants/assets';

// ENV
import { REACT_APP_NEW_INTEGRATIONS_PUE_PAID } from '../../../../constants/app'
import { showNormalMessage } from '../../../../helpers/messages';

const renderTextField = ({ input = {}, label, meta: { touched, error }, type, InputProps }) => (
  <div className='material-form__field-wrapper'>
    <TextField
			id={input.name}
      className='material-form__field'
      variant="outlined"
      label={label}
      error={touched && !!error}
      type={type}
      value={input.value}
      onChange={(e) => {
        e.preventDefault();
        input.onChange(e.target.value);
      }}
      InputProps={ InputProps }
    />
    { touched && error && <span className='form__form-group-error'>{ error }</span> }
  </div>
); // END renderTextField

const renderDatePickerFieldOutlined = ({ input = {}, label, meta: { touched, error }}) => {
  return (
    <div className='material-form__field-wrapper picker'>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={moment.locale("es")}>
        <div>
          <DatePicker
            { ...input }
            className='filters__pickers'
            label={label}
            error={touched && !!error}
            variant='outlined'
            onChange={input.onChange}
            format="DD / MMMM / YYYY"
            maxDate={ moment().subtract(3, 'd') }
            minDate={ moment('2017-07-02T00:00:00+00:00') }
          />
          { touched && error && <span className='form__form-group-error'>{ error }</span> }
        </div>
      </MuiPickersUtilsProvider>
    </div>)
};// END renderDatePickerFieldOutlined

const FormSatCiec = (props) => {
  const { account, integrateDownloads, handleModal, showPassword, setShowPassword, globalRfc, alert } = props;
  const [isLoading, setLoading] = useState(false);
  const [paidPue, setPaidPue] = useState(0);
  const [rfc, setRfc] = useState(null);

  useEffect(() => {
    takeRfc()
    props.initialize({
      'sat_rfc': rfc,
      'sync_start_date': moment().subtract(7, 'd')
    })
  }, [rfc, globalRfc])

  const takeRfc = () => {
    if(get(props, 'globalRfc')) {
      setRfc(get(props, 'globalRfc'))
    } else if(get(props, 'integrations.sat.sat_rfc')) {
      setRfc(get(props, 'integrations.sat.sat_rfc'))
    } else {
      setRfc(get(props, 'integrations.sat_downloads.sat_rfc'))
    }

  } // END takeRfc

  const onSubmit = (form) => {
    setLoading(!isLoading)
    const body = {
      sat_rfc: form.sat_rfc,
      sat_ciec: form.sat_ciec,
      sync_start_date: moment(form.sync_start_date).unix(),
      sync_paid_pue: paidPue
    }

    integrateDownloads(body)
      .then(() => {
        setLoading(!isLoading)
        showNormalMessage({
          message:
            'Estamos procesando la validación de tu CIEC. Este proceso puede tomar un poco de tiempo, pero no te preocupes, te notificaremos vía correo electrónico en cuanto tengamos una respuesta. Agradecemos tu paciencia.',
          color: 'warning',
          duration: 10
        });
      })
      .then(() => handleModal())
      .catch(error => {
        const errorObject = Object.assign({}, error)
        const segmentErrorBody = {
          error_status_code: get(errorObject, 'response.status'),
          error_code       : get(errorObject, 'response.data.code'),
          error_message    : get(errorObject, 'response.data.message')

        }
        segmentTrackEvent('Error On SAT Integration', get(account, 'team.uuid'), segmentErrorBody)
        setLoading(!isLoading)
        props.dispatch({ type: "ERROR", payload: error })
      })
  }

  return (
    <div className="sat__integration">
      <Row className="row__info">
        <Col xs={2}>
          <img src={ SAT_IMG } alt="SAT_logo"/>
        </Col>
        <Col>
          <p>Ingresa tus credenciales CIEC para comenzar la descarga de facturas desde los servicios del SAT, de manera automática.</p>
        </Col>
      </Row>
      <Row className="margin-bottom">
        <Col>
          <p className="rfc__title">RFC</p>
          <p className="rfc__value">{ rfc }</p>
        </Col>
      </Row>
      <form className='material-form stripe' onSubmit={ props.handleSubmit(onSubmit) }>
        <Row className="margin-bottom">
          <Col xs={ 6 }>
            <Field
              name="sync_start_date"
              component={ renderDatePickerFieldOutlined }
              label="Descargar a partir de"
              onChange={ (e) => e.preventDefault }
            />
          </Col>
        </Row>
        <Row>
          <Col xs={ 6 }>
            <p className="rfc__ciec-text">Conexión con tu clave CIEC</p>
          </Col>
          <Col>
            <span className="help-text">
              <HelpCircleIcon/>
              <a target="_blank" rel="noopener noreferrer" href="https://www.sat.gob.mx/tramites/38517/generacion-y-actualizacion-de-contrasena-para-persona-fisica">
                ¿Cómo obtener tu clave CIEC?
              </a>
            </span>
          </Col>
        </Row>
        <hr className="margin-bottom" />
        <Row className="margin-bottom">
          <Col>
            <Field
              name="sat_ciec"
              component={ renderTextField }
              label="Clave CIEC"
              type={ showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    disablePointerEvents={false}
                    >
                      <IconButton style={{padding:0}} onClick={ () => setShowPassword(!showPassword) }>
                        {showPassword ? <EyeOffIcon/> : <EyeIcon/>}
                      </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Col>
        </Row>
        { REACT_APP_NEW_INTEGRATIONS_PUE_PAID === 'true' &&
        <React.Fragment>
          <Row>
            <Col className="sat__pue-paid-switch">
                <div>Sincronizar facturas <span>PUE</span> como pagadas</div><Switch checked={ paidPue === 1 ? true : false} color="primary" onClick={ () => setPaidPue(paidPue === 0 ? 1 : 0) } />
            </Col>
          </Row>
          <Row>
            <Col className="sat__pue-paid-note">
              { !!alert && alert(paidPue) }
            </Col>
          </Row>
        </React.Fragment>
        }
        <Row style={{marginTop:25}}>
          <Col xs={5}>
            <Button
              type='button'
              className='canel-btn'
              onClick={ () => props.handleModal() }>
              CANCELAR
            </Button>
          </Col>
          <Col style={{paddingLeft:0, display:"flex", justifyContent:"flex-end"}}>
            {/* <Button color="success">CONECTAR</Button> */}
            <Button className="save-button" color='success' type='submit' disabled={ isLoading }>
              <LoadingButton isLoading={ isLoading } placeholder='CONECTAR'/>
            </Button>
          </Col>
        </Row>
      </form>
    </div>
  )
}

FormSatCiec.propTypes = {
  account: PropTypes.object.isRequired,
  integrateDownloads: PropTypes.func.isRequired,
  handleModal: PropTypes.func.isRequired,
  showPassword: PropTypes.any,
  setShowPassword: PropTypes.func.isRequired,
  globalRfc: PropTypes.any,
  alert: PropTypes.any,
  initialize: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired
};

const validate = values => {
  const { sat_ciec } = values, errors = {};

  if (!sat_ciec) errors.sat_ciec = 'Proporciona tu clave CIEC'

  return errors;

}; // END validate

export default reduxForm({ form: 'SAT_CIEC_INTEGRATION', validate })(connect(state => ({
  errors             : get(state, 'errors'),
	form               : get(state, 'form.SAT_CIEC_INTEGRATION.values'),
	integrations       : get(state, 'integrations'),
	rfc                : get(state, 'account.profile.tax_profile'),
  account            : get(state, 'account'),
	enableReinitialize : true

}), { promptUser, integrateDownloads })(FormSatCiec)); // END connect export



// WEBPACK FOOTER //
// ./src/containers/account/components/satIntegration/FormSatCiec.js